/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './style.css';
import { useDispatch } from 'react-redux';
import { updateSleepSettings } from 'store/modules/settings';
import { hideDialog } from 'store/modules/base/dialog';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import utils from 'util/utils';
import withBoundary from 'component/ErrorBoundary/withBoundary';

const DigitalSignage = ({
  options,
  baseUrl,
  showLeave = true,
  style = {},
  theme = Light,
  onLeave
}) => {
  const [timer, setTimer] = useState(10);
  const [showTimer, setShowTimer] = useState(false);
  const styles = themedStyle(theme);
  const dispatch = useDispatch();
  const intervalRef = useRef(null);
  useEffect(() => {
    if (showLeave && options?.duration) {
      setShowTimer(true);
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimer((options?.duration ?? 10) * 60);
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () =>
      intervalRef.current ? clearInterval(intervalRef.current) : null;
  }, []);
  useEffect(() => {
    if (timer < 1) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      dispatch(
        updateSleepSettings({
          userDigitalSignageMode: false
        })
      );
      dispatch(hideDialog('DigitalSignage'));
    }
  }, [timer]);
  return (
    <div className="digital-tabs" style={style}>
      {showLeave && (
        <div className="tabs" style={styles.tabs}>
          {showTimer && (
            <Box className="digitaltimerContainer">
              <Box className="digitaltimerLabel">
                {`time remaining ${utils.formatNumbers(
                  utils.getHours(timer)
                )}:${utils.formatNumbers(utils.getMinutes(timer))}`}
              </Box>
            </Box>
          )}
          <Button style={styles.leaveButton} onClick={onLeave}>
            leave digital signage
          </Button>
        </div>
      )}
      <IframeTab
        baseUrl={baseUrl}
        options={options}
        theme={theme}
        showLeave={showLeave}
      />
    </div>
  );
};

const IframeTab = ({ index, baseUrl, theme, showLeave }) => {
  const styles = themedStyle(theme);
  const iframeRef = useRef();

  return (
    <div
      key={index}
      style={{
        ...styles.content,
        flex: 1
      }}>
      <iframe
        ref={iframeRef}
        title={'digital signage'}
        src={baseUrl}
        name="vwbrowser"
        width="100%"
        height="100%"
        target="_parent"
        frameBorder="no"
        sandbox="allow-modals allow-forms allow-popups allow-scripts allow-same-origin"
        style={{
          backgroundColor: 'white',
          pointerEvents: showLeave ? 'all' : 'none'
        }}
        onLoad={() => {
          // Handle iframe load completion and set tab.isLoading accordingly.
        }}
        className="browser-iframe"
      />
    </div>
  );
};
const themedStyle = (theme) => ({
  tabs: {
    display: 'flex',
    position: 'relative',
    justifyContent: 'space-between',
    backgroundColor: theme.topBarColor
  },
  tab: {
    minWidth: '150px',
    maxWidth: '280px',
    overflow: 'hidden',
    display: 'flex'
  },
  activeTab: { zIndex: '40', position: 'relative', paddingBottom: '1px' },
  tabBox: {
    flex: '1',
    minWidth: '100px',
    borderRadius: '4px',
    display: 'flex',
    backgroundColor: theme.tabColor
  },
  activeTabBox: {
    flex: 1,
    display: 'flex',
    marginLeft: 4,
    marginTop: 4,
    width: '100%',
    height: '100%',
    backgroundColor: theme.activeTabColor,
    borderRadius: 5
  },
  content: {
    background: theme.contentColor,
    position: 'relative',
    height: '100%'
  },
  border: {
    margin: '8px 0',
    height: 19,
    borderRight: '1px solid rgb(0 0 0 / 6%)'
  },
  title: {
    color: theme.labelColor,
    fontSize: 12,
    flex: 1,
    marginLeft: 5,
    lineHeight: '15px',
    overflow: 'hidden'
  },
  cross: {
    flexGrow: '0',
    flexShrink: '0',
    position: 'relative',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '8px 8px'
  },
  inputContainer: {
    display: 'flex',
    backgroundColor: 'white',
    padding: 5,
    width: '80%',
    zIndex: 0
  },
  icon: {
    padding: 5
  },
  inputStyle: {
    borderRadius: 100,
    width: '100%',
    borderWidth: 1,
    borderColor: 'grey',
    color: 'black',
    fontFamily: 'SF UI Display, sans-serif',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '12px',
    textTransform: 'lowercase',
    '& label.Mui-focused': {
      color: '#A0AAB4'
    },
    '& .MuiInput-underline:after': {
      borderBottom: 0
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#E0E3E7'
      },
      '&:hover fieldset': {
        borderColor: '#E3E6E9'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#A8C3F8',
        borderWidth: 1
      }
    },
    '& .MuiOutlinedInput-input': {
      borderRadius: 50,
      position: 'relative',
      backgroundColor: '#F3F6F9',
      border: '1px solid',
      borderColor: '#E0E3E7',
      fontSize: 12,
      padding: '5px 10px',
      fontFamily: 'SF UI Display, sans-serif',
      '&.focus': {
        boxShadow: `rgba(168, 195, 248, 0.25) 0 0 0 0.2rem`,
        borderColor: '#A8C3F8'
      },
      '&:hover': {
        backgroundColor: '#E3E6E9',
        borderColor: '#E3E6E9'
      }
    }
  },
  leaveButton: {
    backgroundColor: '#FF5050',
    borderRadius: '12px',
    color: 'white',
    padding: '12px 10px',
    margin: 5,
    fontFamily: 'SF UI Display, sans-serif',
    fontStyle: 'normal',
    fontWeight: 300,
    fontSize: '16px',
    textTransform: 'lowercase',
    position: 'absolute',
    right: 0,
    zIndex: 2
  }
});
export default React.memo(withBoundary(DigitalSignage));
IframeTab.propTypes = {
  options: PropTypes.object,
  baseUrl: PropTypes.string,
  index: PropTypes.number,
  theme: PropTypes.object,
  showLeave: PropTypes.bool
};
DigitalSignage.propTypes = {
  options: PropTypes.object,
  style: PropTypes.object,
  theme: PropTypes.object,
  onLeave: PropTypes.func,
  injectProps: PropTypes.object,
  baseUrl: PropTypes.string,
  showLeave: PropTypes.bool
};
export const Dark = {
  contentColor: '#252729',
  activeTabColor: '#323639',
  topBarColor: '#202124',
  tabColor: 'transparent',
  tabHoverColor: '#292b2e',
  cancelButtonColor: '#71757a',
  cancelButtonHoverColor: 'red',
  cancelActiveButtonColor: '#858b8f',
  cancelActiveButtonHoverColor: 'red',
  labelColor: '#9ca1a7',
  addButtonColor: 'transparent',
  addButtonHoverColor: '#292b2e',
  addButton: '#71757a'
};
export const Light = {
  contentColor: 'white',
  activeTabColor: 'white',
  topBarColor: '#dee1e6',
  tabColor: 'transparent',
  tabHoverColor: '#f4f5f6',
  cancelButtonColor: 'black',
  cancelButtonHoverColor: 'red',
  cancelActiveButtonColor: 'black',
  cancelActiveButtonHoverColor: 'red',
  labelColor: '#45474a',
  addButtonColor: 'transparent',
  addButtonHoverColor: '#ffffff26',
  addButton: '#45474a'
};
